/**
 * Telemetry history — decoding, energy maths, formatting and CSV.
 *
 * Pure functions only (no React, no fetch), so the page, the chart and the
 * Live-view strip all agree on every number.
 *
 * Signs, as everywhere in the portal:
 *   grid     + importing   − exporting
 *   battery  + discharging − charging
 *   load     household use (calculated_load_power_w), never negative in practice
 *
 * Display rules (design v1.2): summed energy in kWh to 1 decimal place;
 * instantaneous power in kW to 2 decimal places. CSV keeps full precision.
 */
import type { HistoryResponse, HistoryResolution } from '../api/historyTypes'

export const UK_TZ = 'Europe/London'
/** A raw reading more than this long after the previous one starts a new line segment. */
const RAW_GAP_S = 90
/** Same rule as production savings.slots(): longer intervals contribute no energy. */
const MAX_INTERVAL_S = 300
const NOMINAL_BUCKET_S: Record<HistoryResolution, number> = { '30s': 30, '5min': 300, hour: 3600, day: 86400 }

/** Chart-ready series. Values in kW (power) and % (SoC); null = gap. */
export interface HistorySeries {
  resolution: HistoryResolution
  t: number[]                       // epoch seconds
  grid: (number | null)[]
  battery: (number | null)[]
  load: (number | null)[]
  soc: (number | null)[]
  gridMin: (number | null)[] | null // aggregated only
  gridMax: (number | null)[] | null
  loadMax: (number | null)[] | null
}

/** Readings as received, for energy maths and CSV (no gap rows). */
export interface HistoryPoints {
  resolution: HistoryResolution
  t: number[]
  bucketS: number[]                 // aggregated: length of each bucket in seconds
  gridW: (number | null)[]          // raw
  batteryW: (number | null)[]
  loadW: (number | null)[]
  socPct: (number | null)[]
  impWh: number[]                   // aggregated
  expWh: number[]
  chgWh: number[]
  disWh: number[]
  loadWh: number[]
  gridMinW: (number | null)[]
  gridMaxW: (number | null)[]
  loadMaxW: (number | null)[]
}

export interface EnergyTotals {
  imported: number   // kWh
  exported: number
  charged: number
  discharged: number
  used: number
}

/** Expand dt[] into absolute times. */
function times(resp: HistoryResponse): number[] {
  const out: number[] = []
  if (resp.t0 == null) return out
  let t = resp.t0
  resp.dt.forEach((d, i) => { t = i === 0 ? resp.t0! : t + d; out.push(t) })
  return out
}

export function toPoints(resp: HistoryResponse): HistoryPoints {
  const t = times(resp)
  const raw = resp.resolution === '30s'
  const nominal = NOMINAL_BUCKET_S[resp.resolution]
  const end = Math.floor(new Date(resp.range_to).getTime() / 1000)
  const bucketS = raw ? [] : t.map((ti, i) => {
    // The next bucket's start bounds this one; the last is cut at the range end
    // (today's bucket is still filling).
    const next = i + 1 < t.length ? t[i + 1] : ti + nominal
    return Math.max(1, Math.min(next - ti, nominal * 1.1, Math.max(end - ti, 1)))
  })
  return {
    resolution: resp.resolution, t, bucketS,
    gridW: resp.grid_w ?? [], batteryW: resp.battery_w ?? [], loadW: resp.load_w ?? [],
    socPct: resp.soc_pct ?? [],
    impWh: resp.grid_import_wh ?? [], expWh: resp.grid_export_wh ?? [],
    chgWh: resp.battery_charge_wh ?? [], disWh: resp.battery_discharge_wh ?? [],
    loadWh: resp.load_wh ?? [],
    gridMinW: resp.grid_min_w ?? [], gridMaxW: resp.grid_max_w ?? [], loadMaxW: resp.load_max_w ?? [],
  }
}

/** Append a live-tail response (30 s) to existing raw points. */
export function appendPoints(base: HistoryPoints, tail: HistoryResponse): HistoryPoints {
  const add = toPoints(tail)
  if (add.t.length === 0) return base
  const last = base.t.length ? base.t[base.t.length - 1] : -Infinity
  const keep = add.t.map((ti) => ti > last)
  const pick = <T,>(xs: T[]) => xs.filter((_, i) => keep[i])
  return {
    ...base,
    t: base.t.concat(pick(add.t)),
    gridW: base.gridW.concat(pick(add.gridW)),
    batteryW: base.batteryW.concat(pick(add.batteryW)),
    loadW: base.loadW.concat(pick(add.loadW)),
    socPct: base.socPct.concat(pick(add.socPct)),
  }
}

const kw = (w: number | null | undefined) => (w == null ? null : w / 1000)

/** Points -> chart series, with a null row at every gap so lines break. */
export function toSeries(p: HistoryPoints): HistorySeries {
  const raw = p.resolution === '30s'
  const gapS = raw ? RAW_GAP_S : NOMINAL_BUCKET_S[p.resolution] * 1.5
  const s: HistorySeries = {
    resolution: p.resolution, t: [], grid: [], battery: [], load: [], soc: [],
    gridMin: raw ? null : [], gridMax: raw ? null : [], loadMax: raw ? null : [],
  }
  for (let i = 0; i < p.t.length; i++) {
    if (i > 0 && p.t[i] - p.t[i - 1] > gapS) {
      s.t.push(p.t[i - 1] + 1)
      s.grid.push(null); s.battery.push(null); s.load.push(null); s.soc.push(null)
      if (!raw) { s.gridMin!.push(null); s.gridMax!.push(null); s.loadMax!.push(null) }
    }
    s.t.push(p.t[i])
    s.soc.push(p.socPct[i] ?? null)
    if (raw) {
      s.grid.push(kw(p.gridW[i])); s.battery.push(kw(p.batteryW[i])); s.load.push(kw(p.loadW[i]))
    } else {
      // Average power over the bucket from its energy: W = Wh x 3600 / seconds
      const f = 3.6 / p.bucketS[i]   // Wh -> kW for this bucket
      s.grid.push((p.impWh[i] - p.expWh[i]) * f)
      s.battery.push((p.disWh[i] - p.chgWh[i]) * f)
      s.load.push(p.loadWh[i] * f)
      s.gridMin!.push(kw(p.gridMinW[i])); s.gridMax!.push(kw(p.gridMaxW[i])); s.loadMax!.push(kw(p.loadMaxW[i]))
    }
  }
  return s
}

/** Energy inside [from, to] (epoch seconds), sign-split like savings.slots(). */
export function energyBetween(p: HistoryPoints, from: number, to: number): EnergyTotals {
  const e: EnergyTotals = { imported: 0, exported: 0, charged: 0, discharged: 0, used: 0 }
  if (p.resolution !== '30s') {
    for (let i = 0; i < p.t.length; i++) {
      // A bucket counts when it starts inside the view; the view's end is the
      // last bucket's start when not zoomed, so the comparison is inclusive.
      if (p.t[i] < from || p.t[i] > to) continue
      e.imported += p.impWh[i]; e.exported += p.expWh[i]
      e.charged += p.chgWh[i]; e.discharged += p.disWh[i]; e.used += p.loadWh[i]
    }
    for (const k of Object.keys(e) as (keyof EnergyTotals)[]) e[k] /= 1000
    return e
  }
  const pos = (v: number | null) => Math.max(v ?? 0, 0)
  const neg = (v: number | null) => Math.max(-(v ?? 0), 0)
  for (let i = 1; i < p.t.length; i++) {
    const a = p.t[i - 1], b = p.t[i]
    if (a < from || b > to) continue
    const dt = b - a
    if (dt <= 0 || dt > MAX_INTERVAL_S) continue
    const k = dt / 3600 / 1000 / 2   // W·s -> kWh, trapezoid
    e.imported += (pos(p.gridW[i]) + pos(p.gridW[i - 1])) * k
    e.exported += (neg(p.gridW[i]) + neg(p.gridW[i - 1])) * k
    e.charged += (neg(p.batteryW[i]) + neg(p.batteryW[i - 1])) * k
    e.discharged += (pos(p.batteryW[i]) + pos(p.batteryW[i - 1])) * k
    e.used += (pos(p.loadW[i]) + pos(p.loadW[i - 1])) * k
  }
  return e
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Summed energy: 1 decimal place. */
export const fmtKwh = (v: number) => v.toFixed(1)
/** Instantaneous power: 2 decimal places. */
export const fmtKw = (v: number | null | undefined) => (v == null ? '–' : `${v.toFixed(2)} kW`)
export const fmtSoc = (v: number | null | undefined) => (v == null ? '–' : `${Math.round(v)}%`)

const fmtTimeOnly = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, hour: '2-digit', minute: '2-digit' })
const fmtDayTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: UK_TZ, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
})
const fmtDay = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, weekday: 'short', day: 'numeric', month: 'short' })
const fmtDayShort = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, day: 'numeric', month: 'short' })
const fmtMonth = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, month: 'short', year: '2-digit' })

export const formatTime = (s: number) => fmtTimeOnly.format(new Date(s * 1000))
export const formatDayTime = (s: number) => fmtDayTime.format(new Date(s * 1000))
export const formatDay = (s: number) => fmtDay.format(new Date(s * 1000))

/** Axis tick labels that suit the visible span. */
const fmtWeekday = new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, weekday: 'short', day: 'numeric' })

export function tickFormatter(spanS: number): (s: number) => string {
  if (spanS <= 36 * 3600) return formatTime
  // Up to a fortnight: the day at UK midnight ("Wed 21"), the time otherwise.
  if (spanS <= 15 * 86400) return (s) => {
    const t = formatTime(s)
    return t === '00:00' ? fmtWeekday.format(new Date(s * 1000)) : t
  }
  if (spanS <= 92 * 86400) return (s) => fmtDayShort.format(new Date(s * 1000))
  return (s) => fmtMonth.format(new Date(s * 1000))
}

/** What one point is, for the readout ("Sat 24 Oct, 14:05" or "Sat 24 Oct"). */
export function pointLabel(resolution: HistoryResolution, s: number): string {
  if (resolution === 'day') return formatDay(s)
  if (resolution === 'hour') return `${formatDayTime(s)}–${formatTime(s + 3600)}`
  if (resolution === '5min') return `${formatDayTime(s)}–${formatTime(s + 300)}`
  return formatDayTime(s)
}

export function rangeLabel(from: number, to: number): string {
  return `${formatDayTime(from)} – ${formatDayTime(to)}`
}

// ---------------------------------------------------------------------------
// Ranges (UK days)
// ---------------------------------------------------------------------------

/** Epoch seconds of UK midnight for the UK date containing `s`, offset by `days`. */
export function ukMidnight(s: number, days = 0): number {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: UK_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(s * 1000)).split('-').map(Number)
  // Start from UTC midnight of that calendar date, then correct for the UK offset (0 or +1 h).
  const utc = Date.UTC(parts[0], parts[1] - 1, parts[2] + days) / 1000
  for (const off of [0, -3600]) {
    const cand = utc + off
    const h = Number(new Intl.DateTimeFormat('en-GB', { timeZone: UK_TZ, hour: '2-digit', hourCycle: 'h23' })
      .format(new Date(cand * 1000)))
    if (h === 0) return cand
  }
  return utc
}

/** yyyy-mm-dd for a UK date, for <input type="date">. */
export function ukDateString(s: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: UK_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(s * 1000))
}

export function ukMidnightOf(dateString: string): number {
  const [y, m, d] = dateString.split('-').map(Number)
  return ukMidnight(Date.UTC(y, m - 1, d, 12) / 1000)
}

// ---------------------------------------------------------------------------
// CSV — full precision, exactly the points in [from, to]
// ---------------------------------------------------------------------------

const iso = (s: number) => new Date(s * 1000).toISOString().replace('.000Z', 'Z')
const cell = (v: number | null | undefined) => (v == null ? '' : String(v))

export function toCsv(p: HistoryPoints, from: number, to: number): string {
  const rows: string[] = []
  if (p.resolution === '30s') {
    rows.push('timestamp_utc,grid_w,battery_w,load_w,soc_pct')
    p.t.forEach((t, i) => {
      if (t < from || t > to) return
      rows.push([iso(t), cell(p.gridW[i]), cell(p.batteryW[i]), cell(p.loadW[i]), cell(p.socPct[i])].join(','))
    })
  } else {
    rows.push('bucket_start_utc,bucket_seconds,grid_import_wh,grid_export_wh,battery_charge_wh,'
      + 'battery_discharge_wh,load_wh,grid_min_w,grid_max_w,load_max_w,soc_end_pct')
    p.t.forEach((t, i) => {
      if (t < from || t > to) return
      rows.push([iso(t), Math.round(p.bucketS[i]), p.impWh[i], p.expWh[i], p.chgWh[i], p.disWh[i], p.loadWh[i],
        cell(p.gridMinW[i]), cell(p.gridMaxW[i]), cell(p.loadMaxW[i]), cell(p.socPct[i])].join(','))
    })
  }
  return rows.join('\r\n') + '\r\n'
}

export function fileStamp(s: number): string {
  return ukDateString(s) + '_' + formatTime(s).replace(':', '')
}

/** Start a browser download of a Blob. */
export function saveBlob(blob: Blob, filename: string): void {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 2000)
}
